import React, { useState } from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings.js";
import { ReactComponent as PlusIcon } from "feather-icons/dist/icons/plus.svg";
import { ReactComponent as MinusIcon } from "feather-icons/dist/icons/minus.svg";
import KeysWithLock from "images/keyservice/keyswithlock.avif";

const Container = tw.div`relative bg-[#f9fafc]`;
const Content = tw.div`max-w-screen-xl mx-auto py-20 px-4 md:px-8`;

const TwoColumn = tw.div`flex flex-col lg:flex-row items-center justify-between`;
const Column = tw.div``;

const ImageWrapper = tw.div`hidden lg:block w-full h-full rounded-full overflow-hidden shadow-md`;
const Image = styled.div(props => [
  `background-image: url("${props.imageSrc}");`,
  tw`bg-cover bg-center w-full h-144`,
]);

const FAQContent = tw.div`lg:ml-12`;
const Subheading = tw(SubheadingBase)`mb-4 text-center lg:text-left text-[#3F62A3]`;
const Heading = tw(SectionHeading)`lg:text-left text-[#002952]`;
const Description = tw.p`max-w-xl text-center mx-auto lg:mx-0 lg:text-left lg:max-w-none leading-relaxed text-sm sm:text-base lg:text-lg font-medium mt-4 text-gray-700`;

const FAQSContainer = tw.dl`mt-12`;
const FAQ = styled.div`
  ${tw`cursor-pointer mt-6 select-none bg-white border border-gray-200 rounded-full p-6 shadow-sm hover:shadow-md transition duration-300`}
`;
const Question = tw.dt`flex justify-between items-center`;
const QuestionText = tw.span`text-base sm:text-lg lg:text-xl font-semibold text-[#002952]`;
const QuestionToggleIcon = styled.span`
  ${tw`ml-2 bg-[#3F62A3] text-white p-2 rounded-full transition-transform duration-300 group-hover:scale-110`}
  svg {
    ${tw`w-4 h-4`}
  }
`;
const Answer = motion(
  tw.dd`pointer-events-none text-sm sm:text-base leading-relaxed text-gray-700 mt-3`
);

export default ({
  subheading = "Veelgestelde vragen",
  heading = <>Veelgestelde <span tw="text-[#3F62A3]">vragen</span></>,
  description = "Vind hier antwoorden op de meest voorkomende vragen over onze diensten. Staat jouw vraag er niet tussen? Neem gerust contact met ons op, we helpen je graag verder.",
  faqs = null,
}) => {
  const defaultFaqs = [
    {
      question: "Welke diensten bieden jullie precies aan?",
      answer:
        "Safety Locks biedt een volledig pakket aan slotenmakersdiensten: van deuropeningen en cilindervervanging tot meerpuntssluitingen en inbraakpreventie. We staan 24/7 voor je klaar.",
    },
    {
      question: "Hoe snel kunnen jullie ter plaatse zijn?",
      answer:
        "In de meeste regio’s zijn we binnen 30 minuten ter plaatse. Dankzij onze landelijke dekking zijn onze monteurs altijd dichtbij.",
    },
    {
      question: "Werken jullie met gecertificeerde materialen?",
      answer:
        "Ja, we gebruiken uitsluitend SKG-gecertificeerde sloten en cilinders van betrouwbare merken zoals Lips, Nemef en Dom.",
    },
    {
      question: "Kan ik ook een offerte vooraf ontvangen?",
      answer:
        "Zeker. Neem contact met ons op via het offerteformulier of telefonisch, dan ontvang je snel een duidelijke prijsindicatie zonder verrassingen achteraf.",
    },
  ];

  if (!faqs || faqs.length === 0) faqs = defaultFaqs;

  const [activeQuestionIndex, setActiveQuestionIndex] = useState(null);
  const toggleQuestion = index =>
    setActiveQuestionIndex(activeQuestionIndex === index ? null : index);

  return (
    <Container id="faq">
      <Content>
        <TwoColumn>
          <Column tw="hidden lg:block w-5/12 flex-shrink-0">
            <ImageWrapper>
              <Image imageSrc={KeysWithLock} />
            </ImageWrapper>
          </Column>
          <Column>
            <FAQContent>
              {subheading && <Subheading>{subheading}</Subheading>}
              <Heading>{heading}</Heading>
              <Description>{description}</Description>

              <FAQSContainer>
                {faqs.map((faq, index) => (
                  <FAQ key={index} onClick={() => toggleQuestion(index)} className="group">
                    <Question>
                      <QuestionText>{faq.question}</QuestionText>
                      <QuestionToggleIcon>
                        {activeQuestionIndex === index ? <MinusIcon /> : <PlusIcon />}
                      </QuestionToggleIcon>
                    </Question>
                    <Answer
                      variants={{
                        open: { opacity: 1, height: "auto" },
                        collapsed: { opacity: 0, height: 0 },
                      }}
                      initial="collapsed"
                      animate={activeQuestionIndex === index ? "open" : "collapsed"}
                      transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                    >
                      {faq.answer}
                    </Answer>
                  </FAQ>
                ))}
              </FAQSContainer>
            </FAQContent>
          </Column>
        </TwoColumn>
      </Content>
    </Container>
  );
};
